import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import VideoModal from '../../../components/ui/VideoModal';
import { homepageTestimonial } from '../../../config/homepageTestimonial';

// Testimonial preview (2026-05-23) — standalone iPhone-proportioned phone
// frame on the white section background. The previous dark-glass card
// wrapper (rgba(10,22,40,0.80) + cyan border + cyan-breath box-shadow)
// was removed because it was visually heavy AND fought with the white
// section above/below it. The cyan-breath effect lives on directly — now
// applied to the phone-frame body itself as a halo glow — so the section
// still has the "alive" cadence without the boxy frame around it.
//
// SCREEN ASPECT — REAL iPHONE:
//   • Inner screen aspect changed from 9:16 → 9:19.5 to match real
//     iPhone proportions (iPhone 15 Pro Max is 1290×2796 = 9:19.5;
//     iPhone 15 is 1179×2556 = also 9:19.5). The video frame now reads
//     as an actual phone instead of a stubbier "phone-ish" rectangle.
//   • The YouTube Short content stays cleanly cropped — the 16:9
//     thumbnail's baked-in black bars are still cut by object-cover
//     (the bars sit on the FAR left/right of the 16:9 image; the 9:16
//     Short content is centered, so a narrower 9:19.5 viewport still
//     shows only the content, just a slightly narrower vertical slice).
//
// WHAT'S PRESERVED:
//   • Click-to-play, modal opens with audio (VideoModal handles).
//   • No autoplay / muted / loop on preview.
//   • Dynamic Island notch + home indicator (matches PhoneDemoSection).
//   • 4s cyan-breath cadence vocabulary (now on the phone, not the card).

const TestimonialVideoBlock = () => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  return (
    <>
      <div className="flex flex-col items-center">
        <button
          ref={triggerRef}
          onClick={() => setIsOpen(true)}
          aria-label={`Watch ${homepageTestimonial.customerName}'s review`}
          className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#07B0E9] focus-visible:ring-offset-4 rounded-[44px]"
        >
          {/* Phone bezel — cyan-breath box-shadow lives here now (was on
              the deleted outer card). The 4s cycle matches the established
              breath vocabulary used on the hero card and StickyCallTab. */}
          <motion.div
            className="relative w-[240px] sm:w-[270px] lg:w-[300px]"
            style={{
              background: '#0a0a0a',
              borderRadius: 44,
              padding: 14,
            }}
            animate={{
              boxShadow: [
                '0 30px 60px rgba(15, 23, 42, 0.18),' +
                  ' 0 8px 20px rgba(15, 23, 42, 0.12),' +
                  ' 0 0 0 rgba(7, 176, 233, 0),' +
                  ' inset 0 0 0 1px rgba(255, 255, 255, 0.06)',
                '0 30px 60px rgba(15, 23, 42, 0.18),' +
                  ' 0 8px 20px rgba(15, 23, 42, 0.12),' +
                  ' 0 0 60px rgba(7, 176, 233, 0.40),' +
                  ' inset 0 0 0 1px rgba(255, 255, 255, 0.06)',
                '0 30px 60px rgba(15, 23, 42, 0.18),' +
                  ' 0 8px 20px rgba(15, 23, 42, 0.12),' +
                  ' 0 0 0 rgba(7, 176, 233, 0),' +
                  ' inset 0 0 0 1px rgba(255, 255, 255, 0.06)',
              ],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', times: [0, 0.5, 1] }}
          >
            {/* Inner screen — REAL iPHONE aspect 9:19.5. */}
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: '9 / 19.5',
                borderRadius: 32,
                background: '#000',
              }}
            >
              <img
                src={homepageTestimonial.thumbnailUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />

              {/* Tap affordance — dimmed background + centered play button. */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform">
                  <Play size={24} className="text-slate-900 ml-1" fill="currentColor" />
                </div>
              </div>

              {/* Dynamic-Island-style notch — small dark pill at the top. */}
              <div
                className="absolute top-2 left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
                style={{ width: 80, height: 18, background: '#000' }}
                aria-hidden="true"
              />

              {/* Home indicator — thin pill at the bottom. */}
              <div
                className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
                style={{ width: 80, height: 4, background: 'rgba(255, 255, 255, 0.55)' }}
                aria-hidden="true"
              />
            </div>
          </motion.div>
        </button>

        {/* Caption — now on the white section bg, so colors flip from
            white→dark. Name matches section heading color (#000150),
            label matches section subhead (gray-500). */}
        <div className="mt-6 text-center">
          <p
            className="font-poppins font-semibold text-base lg:text-lg leading-tight"
            style={{ color: '#000150' }}
          >
            {homepageTestimonial.customerName}
          </p>
          <p className="text-xs font-semibold uppercase tracking-wider mt-1 text-gray-500">
            {homepageTestimonial.label}
          </p>
        </div>
      </div>

      <VideoModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        videoId={homepageTestimonial.videoId}
        title={`${homepageTestimonial.customerName} — ${homepageTestimonial.label}`}
        triggerRef={triggerRef}
        isShort={homepageTestimonial.isShort}
      />
    </>
  );
};

export default TestimonialVideoBlock;
